import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MasonryItem } from './MasonryItem';
import { MasonryGridProps } from './MasonryGrid.types';

export const useMasonryGridLogic = ({ children, itemHeight, outerGap, gap }: MasonryGridProps) => {
  const [layoutWidth, setLayoutWidth] = useState(window?.innerWidth ?? 0);
  const masonryRef = useRef(null);

  const onResize = useCallback(() => {
    if (!!masonryRef.current) {
      setLayoutWidth(masonryRef.current.offsetWidth);
    }
  }, []);

  // - Effect
  useEffect(() => {
    window.addEventListener('resize', onResize);
    if (!!masonryRef.current) onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);
  /*  const { containerStyle, layoutStyle, childElements } = useMemo(() => { */
  // - Attributes
  let childrenArray: (typeof MasonryItem)[] = [];
  const childrenIsArray = Array.isArray(children);
  if (!childrenIsArray) {
    if (children?.type === MasonryItem) {
      childrenArray.push(children);
    }
  } else {
    children.forEach(elem => {
      if (elem?.type === MasonryItem) {
        childrenArray.push(elem);
      } else if (Array.isArray(elem) && elem.every(child => child.type === MasonryItem)) {
        childrenArray = [...childrenArray, ...elem];
      }
    });
  }

  // 1. Items widths (style)
  let rowItemsWidths = [];
  let itemsWidths = [];
  childrenArray.forEach(child => {
    const { width, height } = child.props;
    const itemWidth = (width / height) * itemHeight;
    const sumRowItemsWidths = rowItemsWidths.reduce((sum, w) => sum + w, 0);

    // Has remains space : append item
    if (sumRowItemsWidths + itemWidth <= layoutWidth) {
      rowItemsWidths.push(itemWidth);
      return;
    }

    // Has remains threshold space : append item, calculate, start new empty row
    const remainsWidth = layoutWidth - sumRowItemsWidths;
    if (remainsWidth / itemWidth >= 0.45) {
      rowItemsWidths.push(itemWidth);
      const updatedSumRowItemsWidths = rowItemsWidths.reduce((sum, w) => sum + w, 0);
      const adjustedRowItemsWidths = rowItemsWidths.map((width, index) => {
        const adjustedWidth = (width / updatedSumRowItemsWidths) * layoutWidth;
        if (index == 0) {
          return adjustedWidth - 1;
        }
        return adjustedWidth;
      });
      itemsWidths.push(...adjustedRowItemsWidths);
      rowItemsWidths = [];
      return;
    }

    // No remains threshold space : calculate, start new row with current item
    const adjustedRowItemsWidths = rowItemsWidths.map((width, index) => {
      const adjustedWidth = (width / sumRowItemsWidths) * layoutWidth;
      if (index == 0) {
        return adjustedWidth - 1;
      }
      return adjustedWidth;
    });
    itemsWidths.push(...adjustedRowItemsWidths);
    rowItemsWidths = [itemWidth];
  });
  // Fill remains
  if (rowItemsWidths.length > 0) {
    itemsWidths.push(...rowItemsWidths);
  }

  // 2. Gap
  const containerStyle = {
    padding: outerGap,
    paddingBottom: 20,
  };
  const layoutStyle = {
    marginTop: -(gap / 2),
    marginLeft: -(gap / 2),
    marginRight: -(gap / 2),
  };

  // - Elements
  const childElements = childrenArray.map((child, index) => {
    const itemWidth = itemsWidths[index];
    const itemStyle = {
      width: itemWidth,
      height: itemHeight,
      padding: gap / 2,
    };
    return cloneElement(child, {
      key: `masonry_item_${index}`,
      itemStyle,
    });
  });
  /* return { containerStyle, layoutStyle, childElements };
  }, [children, itemHeight, outerGap, gap, layoutWidth]); */

  return { containerStyle, masonryRef, layoutStyle, childElements };
};
